import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Util } from 'src/shared/util/util';
import { WindowService } from 'src/shared/providers/window.service';
import { Client } from 'src/providers/client/client';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'upsell-settings.html',
    styleUrls: ['./upsell-settings.scss'],
    standalone: false
})
export class UpsellSettingsPage implements OnInit, OnDestroy {
  settingsForm: UntypedFormGroup;
  errorMessage: string;
  dialogParams: any;
  isRightSideContent = true;
  loaded: boolean;
  submitted: boolean;

  
  boundSaveSettings = this.saveSettings.bind(this);

constructor(
    private fb: UntypedFormBuilder,
    private storage: TidyStorage,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private util: Util,
    private windowService: WindowService,
    private client: Client
  ) {
    const t = (key: string) => new TranslationPipe().transform(key);
    this.settingsForm = this.fb.group({
      earlyCheckInInstructions: [t('Please confirm that cleaning will be completed in time for the requested early check-in.'), Validators.required],
      lateCheckOutInstructions: [t('Please ensure the guest understands that late check-out may affect the cleaning schedule.'), Validators.required],
      midStayCleanInstructions: [t('Please schedule the mid-stay clean at a time when the guest will not be present.'), Validators.required]
    });
  }

  async ngOnInit() {
    try {
      this.isRightSideContent =
        (await this.storage.retrieve('dialog-right-side-open')) || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');

      this.rightSidePanelService.setDialogPageTitle(new TranslationPipe().transform('Upsell Settings'));
      this.loadData();
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async loadData() {
    const loading = await this.util.showLoading();
    try {
      // Load saved settings from client metadata - fetch individually since bulk fetch returns empty array
      const metadataKeys = ['external.upsell_early_checkin_instructions', 'external.upsell_late_checkout_instructions', 'external.upsell_midstay_clean_instructions'];

      try {
        // Fetch each metadata key individually
        const metadataPromises = metadataKeys.map(key => this.client.getMetadata([key]));
        const metadataResponses = await Promise.all(metadataPromises);

        const settings: any = {};

        metadataResponses.forEach((response, _index) => {
          if (response && response.length > 0) {
            const item = response[0];
            if (item.key === 'external.upsell_early_checkin_instructions') {
              settings.earlyCheckInInstructions = item.value;
            } else if (item.key === 'external.upsell_late_checkout_instructions') {
              settings.lateCheckOutInstructions = item.value;
            } else if (item.key === 'external.upsell_midstay_clean_instructions') {
              settings.midStayCleanInstructions = item.value;
            }
          }
        });

        // Only patch values that were found in metadata
        if (Object.keys(settings).length > 0) {
          this.settingsForm.patchValue(settings);
        }
      } catch (_metadataError) {
        // Intentionally empty - errors handled elsewhere
      }

      loading.dismiss();
      this.loaded = true;
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
      loading.dismiss();
    }
  }

  async saveSettings() {
    this.submitted = true;

    if (this.settingsForm.valid) {
      try {
        const settings = this.settingsForm.value;

        // Save each setting as separate metadata entries
        const metadataPromises = [
          this.client.addMetadata({
            key: 'upsell_early_checkin_instructions',
            value: settings.earlyCheckInInstructions
          }),
          this.client.addMetadata({
            key: 'upsell_late_checkout_instructions',
            value: settings.lateCheckOutInstructions
          }),
          this.client.addMetadata({
            key: 'upsell_midstay_clean_instructions',
            value: settings.midStayCleanInstructions
          })
        ];

        await Promise.all(metadataPromises);

        this.util.showSuccess(new TranslationPipe().transform('Upsell settings saved successfully'), 3000);

        // Close panel or navigate back
        if (this.isRightSideContent) {
          this.rightSidePanelService.closeRightSidePanel();
        } else {
          this.navCtrl.navigateRoot('upsells');
        }
      } catch (err) {
        this.errorMessage = err.error ? err.error.message : err.message;
      }
    } else {
      this.util.showError(new TranslationPipe().transform('Please fill in all required fields'), 3000);
    }
  }

  cancel() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateRoot('upsells');
    }
  }

  ngOnDestroy() {
    // Clean up any necessary data when component is destroyed
  }

  /**
   * Static method to retrieve upsell settings from metadata
   * Can be used by other components to access upsell settings
   */
  static async getUpsellSettings(client: Client): Promise<{
    earlyCheckInInstructions?: string;
    lateCheckOutInstructions?: string;
    midStayCleanInstructions?: string;
  }> {
    try {
      const metadataKeys = ['external.upsell_early_checkin_instructions', 'external.upsell_late_checkout_instructions', 'external.upsell_midstay_clean_instructions'];

      // Fetch each metadata key individually since bulk fetch returns empty array
      const metadataPromises = metadataKeys.map(key => client.getMetadata([key]));
      const metadataResponses = await Promise.all(metadataPromises);

      if (metadataResponses && metadataResponses.length > 0) {
        const settings: any = {};

        metadataResponses.forEach((response, _index) => {
          if (response && response.length > 0) {
            const item = response[0];
            if (item.key === 'external.upsell_early_checkin_instructions') {
              settings.earlyCheckInInstructions = item.value;
            } else if (item.key === 'external.upsell_late_checkout_instructions') {
              settings.lateCheckOutInstructions = item.value;
            } else if (item.key === 'external.upsell_midstay_clean_instructions') {
              settings.midStayCleanInstructions = item.value;
            }
          }
        });

        return settings;
      }
    } catch (_error) {
      // Intentionally empty - errors handled elsewhere
    }

    // Return default values if no metadata found
    const t = (key: string) => new TranslationPipe().transform(key);
    return {
      earlyCheckInInstructions: t('Please confirm that cleaning will be completed in time for the requested early check-in.'),
      lateCheckOutInstructions: t('Please ensure the guest understands that late check-out may affect the cleaning schedule.'),
      midStayCleanInstructions: t('Please schedule the mid-stay clean at a time when the guest will not be present.')
    };
  }

  /**
   * Static method to save a specific upsell setting
   * Can be used by other components to update individual settings
   */
  static async saveUpsellSetting(client: Client, key: string, value: string): Promise<void> {
    try {
      await client.addMetadata({ key, value });
    } catch (error) {
      throw error;
    }
  }
}
